/**
 * Buoy.ts — floating navigation / mooring buoy.
 *
 * Models a typical hard-plastic mooring buoy as a vertical ellipsoid
 * (axis-aligned), with horizontal radius `radiusXZ` and vertical half-height
 * `radiusY`. The default geometry matches a real "Nuova Rade" type buoy:
 *   • diameter 25 cm  → radiusXZ = 0.125 m
 *   • height   39 cm  → radiusY  = 0.195 m
 *   • full displacement ≈ ρ·V = 1000 · (4/3)π·rXZ²·rY ≈ 12.8 kg
 *     (manufacturer rates "wyporność 9.5 kg" — usable load with safety margin)
 *   • plastic shell mass ≈ 1.5 kg → buoy sits high, most of body above water
 *
 * Coordinate convention: world frame, +X east, +Y up, +Z south.
 *
 * Buoyancy is computed via the ellipsoid-cap formula. We stretch the body
 * along Y so it becomes a sphere of radius rXZ, evaluate the spherical cap
 * volume there, then un-stretch (multiply by rY/rXZ).
 */

import type { WaterField } from "./WaterField";
import type { MooringAnchor } from "./Dock";
import type { BuoyKind } from "./BuoyLibrary";

/** Default buoy params: round Ø40 cm mooring float, orange. */
const DEFAULTS = {
  radiusXZ: 0.2,
  radiusY: 0.2,
  // Mass tuned so equilibrium has ~⅔ of the buoy below the waterline
  // (only the top 30-35 % is visible above water — typical for orange
  // mooring floats). Full submerged displacement V = (4/3)π·r³ ≈ 33.5 kg
  // at r = 0.2 m, so mass ≈ 22 kg → ~66 % submerged.
  mass: 22,
  cHeave: 25, // vertical drag coefficient
  dragCoeffXZ: 18, // lateral drag (skin friction)
} as const;

export interface BuoyOptions {
  posX: number;
  posZ: number;
  /** Horizontal radius (m). Default 0.125. */
  radiusXZ?: number;
  /** Vertical half-height (m). Default 0.195. */
  radiusY?: number;
  /** Mass (kg). Default 1.5. */
  mass?: number;
  /** Display colour. */
  color?: [number, number, number];
  /** Stable identifier (e.g. "moor_bow_anchor"). */
  id?: string;
  /**
   * If true, the buoy is moored to the seabed at its spawn (posX,posZ).
   * It still bobs vertically with waves but does NOT drift horizontally —
   * external XZ forces are absorbed by the (idealised) ground tackle.
   * Default: true.
   */
  anchored?: boolean;
  /**
   * Variant tag selecting the GLB mesh + visual style. Defaults to
   * `"mooring_standard"` (orange plastic mooring buoy).
   */
  kind?: BuoyKind;
  /** World-frame yaw (rad) — applied to the visual mesh. Default 0. */
  headingY?: number;
}

const G = 9.81;
const RHO_WATER = 1000;

export class Buoy implements MooringAnchor {
  /** World-space horizontal position. */
  posX: number;
  posZ: number;
  /** Vertical position of the centre. */
  posY: number;
  /** Vertical velocity. */
  vy = 0;
  /** Horizontal velocities — buoy can drift if it gets pushed. */
  vx = 0;
  vz = 0;

  readonly radiusXZ: number;
  readonly radiusY: number;
  readonly mass: number;
  readonly id: string;
  readonly color: [number, number, number];
  readonly kind: BuoyKind;
  /** World-frame yaw of the visual mesh (rad). */
  headingY: number;
  /** When true, XZ is locked to the spawn anchor position. */
  anchored: boolean;

  /** Accumulated external force (world frame, N). Cleared each `step`. */
  private extFx = 0;
  private extFy = 0;
  private extFz = 0;

  constructor(opts: BuoyOptions) {
    this.posX = opts.posX;
    this.posZ = opts.posZ;
    this.radiusXZ = opts.radiusXZ ?? DEFAULTS.radiusXZ;
    this.radiusY = opts.radiusY ?? DEFAULTS.radiusY;
    this.mass = opts.mass ?? DEFAULTS.mass;
    this.id = opts.id ?? "buoy";
    this.color = opts.color ?? [0.95, 0.35, 0.08]; // bright navigation orange
    this.kind = opts.kind ?? "mooring_standard";
    this.headingY = opts.headingY ?? 0;
    this.anchored = opts.anchored ?? true;
    this.posY = 0;
  }

  /** Total displaced volume if fully submerged (m³). */
  get fullVolume(): number {
    return (4 / 3) * Math.PI * this.radiusXZ * this.radiusXZ * this.radiusY;
  }

  /** MooringAnchor: stable id used by UI/state. */
  get anchorId(): string {
    return this.id;
  }
  /** MooringAnchor: world position the rope attaches to (top of the eye). */
  getWorldPos(): [number, number, number] {
    return [this.posX, this.posY + this.radiusY, this.posZ];
  }

  /** External force applied at the buoy centre (world frame, N). */
  applyExternalForce(fx: number, fy: number, fz: number): void {
    this.extFx += fx;
    this.extFy += fy;
    this.extFz += fz;
  }

  /**
   * Integrate one step. Reads water height at the buoy XZ via the supplied
   * `WaterField`, computes Archimedes restoring force based on the
   * ellipsoid-cap submerged volume, plus heave damping.
   */
  step(dt: number, water: WaterField, waterLevel: number): void {
    const surfY = waterLevel + water.sampleHeight(this.posX, this.posZ);
    const a = this.radiusXZ;
    const b = this.radiusY;
    // Submerged depth (real units) measured from the bottom of the buoy.
    const h = Math.max(0, Math.min(2 * b, surfY - (this.posY - b)));
    // Convert to spherical-cap depth in y-stretched coords (sphere radius = a).
    const hStretched = h * (a / b);
    // Spherical cap volume V_stretched = π h² (3a − h) / 3.
    const vCapStretched =
      (Math.PI * hStretched * hStretched * (3 * a - hStretched)) / 3;
    // Un-stretch: real volume = stretched volume × (b/a).
    const vSub = vCapStretched * (b / a);

    const fBuoy = RHO_WATER * G * vSub;
    const fGrav = -this.mass * G;
    const fDamp = -DEFAULTS.cHeave * this.vy;
    const fy = fGrav + fBuoy + fDamp + this.extFy;
    this.vy += (fy / this.mass) * dt;
    this.posY += this.vy * dt;

    if (this.anchored) {
      // Moored to the seabed: ground tackle absorbs horizontal forces.
      this.vx = 0;
      this.vz = 0;
    } else {
      // Horizontal: simple linear drag + external. Buoy doesn't surf waves.
      const fx = this.extFx - DEFAULTS.dragCoeffXZ * this.vx;
      const fz = this.extFz - DEFAULTS.dragCoeffXZ * this.vz;
      this.vx += (fx / this.mass) * dt;
      this.vz += (fz / this.mass) * dt;
      this.posX += this.vx * dt;
      this.posZ += this.vz * dt;
    }

    // Reset external accumulators.
    this.extFx = 0;
    this.extFy = 0;
    this.extFz = 0;
  }
}
